// src/lib/appcheck.js
import { getApp } from "firebase/app";
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  ReCaptchaV3Provider,
  getToken,
  // getAppCheck, // (optional) if you want to fetch existing instance later
} from "firebase/app-check";

let appCheckInstance = null;
let tokenRequestPromise = null;
let didAttachGestureRetry = false;

function withTimeout(promise, ms) {
  let timeout;
  const deadline = new Promise((resolve) => {
    timeout = setTimeout(() => resolve(null), ms);
  });
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timeout));
}

async function prefetchToken({ forceRefresh = false, timeoutMs = 6500 } = {}) {
  if (!appCheckInstance) return null;
  try {
    const res = await withTimeout(getToken(appCheckInstance, forceRefresh), timeoutMs);
    return res && res.token ? res.token : null;
  } catch {
    return null;
  }
}

function requestCurrentToken({forceRefresh = false, timeoutMs = 6500} = {}) {
  if (tokenRequestPromise) return tokenRequestPromise;
  const request = prefetchToken({forceRefresh, timeoutMs});
  tokenRequestPromise = request.finally(() => {
    if (tokenRequestPromise === request || tokenRequestPromise === wrapped) {
      tokenRequestPromise = null;
    }
  });
  const wrapped = tokenRequestPromise;
  return wrapped;
}

export function createAppCheckProvider(providerName, siteKey) {
  if (providerName === "enterprise") {
    return new ReCaptchaEnterpriseProvider(siteKey);
  }
  if (providerName === "v3") {
    return new ReCaptchaV3Provider(siteKey);
  }
  throw new Error(`Unsupported App Check provider: ${providerName || "missing"}`);
}

function initializeConfiguredAppCheck(siteKey, providerName) {
  const app = getApp();
  return initializeAppCheck(app, {
    provider: createAppCheckProvider(providerName, siteKey),
    isTokenAutoRefreshEnabled: true,
  });
}

function attachOneTimeGestureRetry(siteKey, providerName) {
  if (didAttachGestureRetry) return;
  didAttachGestureRetry = true;

  const retry = async () => {
    try {
      // If app check never initialized (or got wiped), try again
      if (!appCheckInstance) {
        try {
          appCheckInstance = initializeConfiguredAppCheck(siteKey, providerName);
        } catch {
          // ignore
        }
      }

      // Try to fetch token again after user gesture
      await requestCurrentToken({forceRefresh: true, timeoutMs: 6500});
    } catch {
      // ignore
    } finally {
      window.removeEventListener("pointerdown", retry, true);
      window.removeEventListener("keydown", retry, true);
      window.removeEventListener("touchstart", retry, true);
    }
  };

  // capture phase: runs even if UI overlays exist
  window.addEventListener("pointerdown", retry, true);
  window.addEventListener("touchstart", retry, true);
  window.addEventListener("keydown", retry, true);
}

/**
 * Initialize App Check once per page (browser only).
 * FAIL-OPEN: never throws, never blocks rendering.
 */
export function initAppCheck(
  siteKey,
  {providerName, forceRefresh = false, debugToken = null, prefetch = true} = {},
) {
  if (typeof window === "undefined") return;
  if (!siteKey) return;

  try {
    if (debugToken && window.FIREBASE_APPCHECK_DEBUG_TOKEN == null) {
      window.FIREBASE_APPCHECK_DEBUG_TOKEN = debugToken;
    }
    if (!appCheckInstance) {
      // initializeAppCheck throws if already initialized; that’s okay
      try {
        appCheckInstance = initializeConfiguredAppCheck(siteKey, providerName);
      } catch {
        // already initialized or blocked; ignore
      }
    }

    // Provider registration must happen before protected Firebase services make
    // their first request. The explicit prefetch remains delayed by the caller.
    if (prefetch && !tokenRequestPromise) {
      requestCurrentToken({forceRefresh, timeoutMs: 6500}).then((tok) => {
        // If token failed, schedule a one-time retry on user gesture
        if (!tok) attachOneTimeGestureRetry(siteKey, providerName);
        return tok;
      });
    }
  } catch {
    // fail-open
  }
}

/**
 * Wait briefly for an App Check token. If slow/flaky, return null.
 * You can call this before sensitive writes, but DO NOT block UI on it.
 */
export async function ensureAppCheck(ms = 2500, {forceRefresh = false} = {}) {
  try {
    if (!appCheckInstance) return null;
    // Share only an in-flight exchange. Once it settles, discard the promise
    // so a later protected request asks the Firebase SDK for a current token
    // instead of reusing a token string that may have expired meanwhile.
    return await withTimeout(requestCurrentToken({forceRefresh, timeoutMs: ms}), ms);
  } catch {
    return null;
  }
}
